'use client';

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Heart, X } from 'lucide-react';
import { GsImage, cn } from '@/components/help-center/latest-power-shared';
import { formatPowerDisplayName } from '@/lib/power-mention-data';

const MAX_STACK = 5;
const AUTO_DISMISS_MS = 3400;

type WishlistToastKind = 'wish-added' | 'wish-removed' | 'owned-added' | 'owned-removed' | 'already-owned';

type ToastItem = {
  id: string;
  kind: WishlistToastKind;
  powerName: string;
  smileyCode: string;
};

type ShowFairtradeCollectionToastArgs = {
  kind: WishlistToastKind;
  powerName: string;
  smileyCode?: string;
};

type Listener = (items: ToastItem[]) => void;

let stack: ToastItem[] = [];
const listeners = new Set<Listener>();
const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

function emit() {
  const snapshot = [...stack];
  listeners.forEach((listener) => listener(snapshot));
}

function dismissToast(id: string) {
  const timer = dismissTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    dismissTimers.delete(id);
  }
  if (!stack.some((item) => item.id === id)) return;
  stack = stack.filter((item) => item.id !== id);
  emit();
}

function scheduleDismiss(id: string) {
  const existing = dismissTimers.get(id);
  if (existing) clearTimeout(existing);
  dismissTimers.set(
    id,
    setTimeout(() => dismissToast(id), AUTO_DISMISS_MS)
  );
}

function toastCopy(kind: WishlistToastKind): { eyebrow: string; suffix: string } {
  switch (kind) {
    case 'wish-added':
      return { eyebrow: 'Lista de desejos', suffix: 'adicionado aos desejos' };
    case 'wish-removed':
      return { eyebrow: 'Lista de desejos', suffix: 'removido dos desejos' };
    case 'owned-added':
      return { eyebrow: 'Lista de desejos', suffix: 'marcado como conquistado' };
    case 'owned-removed':
      return { eyebrow: 'Lista de desejos', suffix: 'removido da coleção' };
    case 'already-owned':
      return { eyebrow: 'Lista de desejos', suffix: 'já está na sua coleção' };
  }
}

function FairtradeCollectionToastCard({
  item,
  onClose,
}: {
  item: ToastItem;
  onClose: () => void;
}) {
  const displayName = formatPowerDisplayName(item.powerName);
  const { eyebrow, suffix } = toastCopy(item.kind);
  const filledHeart = item.kind === 'wish-added' || item.kind === 'owned-added';

  return (
    <div
      className={cn(
        'group relative w-full overflow-hidden rounded-2xl border border-solid text-left',
        'border-rose-400/70 bg-gradient-to-br from-rose-500/30 via-[#2a0a18] to-[#14040c]',
        'shadow-[0_0_24px_rgba(244,63,94,0.28),0_10px_28px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.08)]'
      )}
      role="status"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-rose-400/15 via-transparent to-fuchsia-600/10"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -inset-px rounded-2xl"
        style={{
          background:
            'radial-gradient(120% 80% at 85% 100%, rgba(251,113,133,0.32), transparent 55%)',
        }}
        aria-hidden
      />

      <Heart
        className={cn(
          'pointer-events-none absolute -bottom-6 -right-1.5 h-20 w-20 rotate-[12deg]',
          filledHeart
            ? 'fill-rose-400/30 stroke-rose-400/25 text-rose-400/30'
            : 'fill-rose-400/15 stroke-rose-400/20 text-rose-400/20'
        )}
        strokeWidth={1.25}
        aria-hidden
      />

      <div className="relative z-10 flex items-center gap-3 px-3.5 py-3 pr-11">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-rose-400/40 bg-[#14040c]/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
          <GsImage
            code={item.smileyCode}
            sizePx={40}
            className="flex h-9 w-9 items-center justify-center"
            imgClassName="max-h-9 max-w-9 object-contain"
          />
        </div>

        <div className="min-w-0 flex-1">
          <p className="font-poppins text-[10px] font-semibold uppercase tracking-[0.12em] text-rose-200/80">
            {eyebrow}
          </p>
          <p className="mt-0.5 font-poppins text-sm leading-snug text-rose-100/90">
            <span className="font-bold text-rose-50">{displayName}</span>{' '}
            <span className="font-medium text-rose-100/80">{suffix}</span>
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={onClose}
        className={cn(
          'absolute right-2.5 top-2.5 z-20 flex h-8 w-8 items-center justify-center rounded-lg',
          'border border-solid border-rose-400/25',
          'bg-gradient-to-br from-[#2a0a18]/95 via-[#1a0610]/95 to-[#0c0408]/95',
          'text-rose-200 shadow-[0_2px_10px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(251,113,133,0.12)]',
          'backdrop-blur-sm transition-all duration-200',
          'hover:border-rose-400/40 hover:from-[#3d1224] hover:via-[#2a0a18] hover:to-[#1a0610]',
          'hover:[&_svg]:text-rose-100',
          'active:scale-95',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0610]'
        )}
        aria-label="Fechar notificação"
      >
        <X className="h-3.5 w-3.5 text-rose-200" strokeWidth={2.5} aria-hidden />
      </button>
    </div>
  );
}

/**
 * Pilha vertical: o card novo entra em baixo; os anteriores sobem por cima dele.
 * Entrada/saída com slide (estilo MSN).
 */
export function FairtradeWishlistToastHost() {
  const [items, setItems] = useState<ToastItem[]>(stack);

  useEffect(() => {
    const listener: Listener = (next) => setItems(next);
    listeners.add(listener);
    setItems([...stack]);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return (
    <div
      className="pointer-events-none fixed bottom-6 right-6 z-[210] flex w-[min(100vw-2rem,22rem)] flex-col-reverse gap-2.5"
      aria-live="polite"
      aria-relevant="additions removals"
    >
      <AnimatePresence initial={false} mode="popLayout">
        {items.map((item) => (
          <motion.div
            key={item.id}
            layout
            className="pointer-events-auto w-full"
            initial={{ opacity: 0, y: 56, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
              opacity: 0,
              y: -48,
              scale: 0.96,
              transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
            }}
            transition={{
              type: 'spring',
              stiffness: 420,
              damping: 32,
              mass: 0.8,
              layout: { type: 'spring', stiffness: 380, damping: 34 },
            }}
          >
            <FairtradeCollectionToastCard
              item={item}
              onClose={() => dismissToast(item.id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

/** Empurra card para a pilha (máx. 5). */
export function showFairtradeCollectionToast({
  kind,
  powerName,
  smileyCode,
}: ShowFairtradeCollectionToastArgs) {
  const code = (smileyCode || powerName).trim().toLowerCase() || 'smile';
  const id = `fairtrade-collection-${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const next: ToastItem = {
    id,
    kind,
    powerName,
    smileyCode: code,
  };

  stack = [...stack, next];
  if (stack.length > MAX_STACK) {
    const removed = stack.slice(0, stack.length - MAX_STACK);
    for (const old of removed) {
      const timer = dismissTimers.get(old.id);
      if (timer) {
        clearTimeout(timer);
        dismissTimers.delete(old.id);
      }
    }
    stack = stack.slice(-MAX_STACK);
  }
  emit();
  scheduleDismiss(id);
}
